import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';
import { useChartTheme } from '../themes/chartTheme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import { determineAggregation } from '../helpers/chartUtils';
import LoadingSpinner from './LoadingSpinner';

interface LeadStatusData {
  date: string;
  new: number;
  duplicate: number;
  error: number;
}

interface LeadStatusStackedChartProps {
  selectedCustomers: string[];
  selectedClients: number[];
}

const getISOWeek = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const formatDateForAggregation = (dateStr: string, aggregation: string): string => {
  const date = new Date(dateStr);

  switch (aggregation) {
    case 'monthly':
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
      });
    case 'weekly':
      const weekStart = new Date(date);
      const dayOfWeek = date.getDay() || 7;
      weekStart.setDate(date.getDate() - dayOfWeek + 1);
      return `KW ${getISOWeek(weekStart)}, ${weekStart.toLocaleDateString('de-DE', { month: 'short' })}`;
    case 'daily':
    default:
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      });
  }
};

const LeadStatusStackedChart: React.FC<LeadStatusStackedChartProps> = ({
  selectedCustomers,
  selectedClients,
}) => {
  const chartTheme = useChartTheme();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<LeadStatusData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'absolute' | 'relative'>('absolute');

  const aggregation = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return 'daily';
    return determineAggregation(filterState.startDate, filterState.endDate);
  }, [filterState.startDate, filterState.endDate]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (
        !filterState.startDate ||
        !filterState.endDate ||
        selectedCustomers.length === 0 ||
        selectedClients.length === 0
      ) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          start_date: dateToSqlDate(filterState.startDate),
          end_date: dateToSqlDate(filterState.endDate),
          customers: selectedCustomers.join(','),
          clients: selectedClients.join(','),
        };

        const [newLeads, duplicateLeads, errorLeads] = await Promise.all([
          fetchData<any[]>('/api/daily-controlling/multi-customer-client-data', {
            ...options,
            lead_status: 'new',
          }),
          fetchData<any[]>('/api/daily-controlling/multi-customer-client-data', {
            ...options,
            lead_status: 'duplicate',
          }),
          fetchData<any[]>('/api/daily-controlling/multi-customer-client-data', {
            ...options,
            lead_status: 'error',
          }),
        ]);

        const dateMap = new Map<string, LeadStatusData>();

        const processData = (dataset: any[], status: 'new' | 'duplicate' | 'error') => {
          dataset.forEach((item) => {
            const aggregatedDate =
              aggregation !== 'daily'
                ? formatDateForAggregation(item.date, aggregation)
                : item.date;
            if (!dateMap.has(aggregatedDate)) {
              dateMap.set(aggregatedDate, {
                date: aggregatedDate,
                new: 0,
                duplicate: 0,
                error: 0,
              });
            }
            const entry = dateMap.get(aggregatedDate)!;
            entry[status] += item.count || 0;
          });
        };

        processData(newLeads, 'new');
        processData(duplicateLeads, 'duplicate');
        processData(errorLeads, 'error');

        const aggregatedData = Array.from(dateMap.values()).sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );

        setData(aggregatedData);
      } catch (err: any) {
        console.error('Error fetching lead status data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [filterState.startDate, filterState.endDate, selectedCustomers, selectedClients, aggregation]);

  const chartData = useMemo(() => {
    if (viewMode === 'absolute') {
      return data;
    }

    return data.map((item) => {
      const total = item.new + item.duplicate + item.error;
      if (total === 0) {
        return { date: item.date, new: 0, duplicate: 0, error: 0 };
      }
      return {
        date: item.date,
        new: (item.new / total) * 100,
        duplicate: (item.duplicate / total) * 100,
        error: (item.error / total) * 100,
      };
    });
  }, [data, viewMode]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'absolute' | 'relative' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box p={2} color="error.main">
        Error loading chart: {error}
      </Box>
    );
  }

  if (selectedCustomers.length === 0 || selectedClients.length === 0) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Wählen Sie mindestens einen Kunden und Client aus, um Daten anzuzeigen.
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Keine Daten für den ausgewählten Zeitraum verfügbar.
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: colors.grey[100],
              borderColor: colors.primary[300],
              '&.Mui-selected': {
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
              },
            },
          }}
        >
          <ToggleButton value="absolute">Absolut</ToggleButton>
          <ToggleButton value="relative">Relativ (%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box height="400px">
        <ResponsiveBar
          data={chartData as any}
          keys={['new', 'duplicate', 'error']}
          indexBy="date"
          theme={chartTheme}
          margin={{ top: 20, right: 130, bottom: 60, left: 80 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={[colors.greenAccent[500], colors.redAccent[400], colors.redAccent[700]]}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: -30,
            legend: 'Datum',
            legendPosition: 'middle',
            legendOffset: 50,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: viewMode === 'absolute' ? 'Anzahl Leads' : 'Anteil (%)',
            legendPosition: 'middle',
            legendOffset: -60,
            format: (value) => (viewMode === 'relative' ? `${value.toFixed(0)}%` : value),
          }}
          valueFormat={(value) =>
            viewMode === 'relative' ? `${value.toFixed(0)}%` : value.toString()
          }
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 12,
              symbolShape: 'square',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
              data: [
                {
                  id: 'new',
                  label: 'Neue Leads',
                  color: colors.greenAccent[500],
                },
                {
                  id: 'duplicate',
                  label: 'Duplikate',
                  color: colors.redAccent[400],
                },
                { id: 'error', label: 'Fehler', color: colors.redAccent[700] },
              ],
            },
          ]}
          tooltip={({ id, value, indexValue }) => (
            <Box
              sx={{
                padding: '8px 12px',
                color: '#222',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <strong>{indexValue}</strong>
              <br />
              {id === 'new' && 'Neue Leads'}
              {id === 'duplicate' && 'Duplikate'}
              {id === 'error' && 'Fehler'}:{' '}
              {viewMode === 'absolute' ? value.toLocaleString('de-DE') : `${value.toFixed(1)}%`}
            </Box>
          )}
          role="application"
          ariaLabel="Lead Status Stacked Bar Chart"
        />
      </Box>
    </Box>
  );
};

export default LeadStatusStackedChart;
